import { useState } from 'react';
import { Button, Flex } from '@mantine/core';
import { TagRes } from '@dealstack/shared/lib/generated';
import { MantineTable } from '@dealstack/frontend/shared/components/Table/MantineTable/MantineTable.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import {
	PermissionWrapper,
	usePermissionRequest,
} from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import { ConfirmDeleteDrawer } from '@dealstack/frontend/shared/components/ConfirmDeleteDrawer/ConfirmDeleteDrawer.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import {
	useTagsList,
	useCreateTag,
	useDeleteTag,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/hooks/useTags.ts';
import { useTagsTableState } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/components/TagsTab/useTagsTableState.ts';
import { CreateTagDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Classifiers/components/TagsTab/CreateTagDrawer.tsx';

export const TagsTab = () => {
	const { mipId } = useRequiredParams();
	const { hasPermission: canEdit } = usePermissionRequest({
		resourceType: ResourceType.PROGRAMME,
		resourceId: mipId,
		permissionRole: PermissionRole.ADMIN,
	});
	const tMipManage = useTranslationMipManage();

	const [isCreateOpen, setIsCreateOpen] = useState(false);
	const [tagToDelete, setTagToDelete] = useState<TagRes | null>(null);
	const [deleteDidSucceed, setDeleteDidSucceed] = useState(false);

	const {
		data: tags = [],
		isLoading: isTagsLoading,
		isFetching: isTagsFetching,
		refetch: refetchTags,
	} = useTagsList({ mipId, isArchived: false });

	const { mutate: createTag, isPending: isCreating } = useCreateTag({ mipId });
	const { mutate: deleteTag, isPending: isDeleting } = useDeleteTag({ mipId });

	const handleDeleteRequest = (tag: TagRes) => {
		setDeleteDidSucceed(false);
		setTagToDelete(tag);
	};

	const { columns, mantineTableDataOperations } = useTagsTableState({
		onDelete: handleDeleteRequest,
		canEdit,
	});

	const handleCreate = (name: string) => {
		createTag(
			{ name },
			{
				onSuccess: () => {
					setIsCreateOpen(false);
					void refetchTags();
				},
			}
		);
	};

	const closeDeleteConfirm = () => {
		setTagToDelete(null);
		setDeleteDidSucceed(false);
	};

	const handleConfirmDelete = () => {
		if (!tagToDelete) {
			return;
		}
		deleteTag(
			{ tagId: tagToDelete.id },
			{
				onSuccess: () => {
					setDeleteDidSucceed(true);
					void refetchTags();
				},
			}
		);
	};

	const isLoading = isTagsLoading || isTagsFetching || isCreating || isDeleting;

	const attachedCohortCount = tagToDelete?.classifiers.length ?? 0;

	return (
		<Flex direction="column" w="100%" h="100%" p="1rem" gap="1rem">
			<Flex justify="flex-end" align="center">
				<PermissionWrapper
					resourceType={ResourceType.PROGRAMME}
					resourceId={mipId}
					permissionRole={PermissionRole.ADMIN}
				>
					<Button
						onClick={() => setIsCreateOpen(true)}
						leftSection={<Icon variant="plus" size="lg" />}
					>
						{tMipManage('root.classifiers.tags.list.create_button')}
					</Button>
				</PermissionWrapper>
			</Flex>

			{!isLoading && !tags.length ? (
				<EmptyState
					title={tMipManage('root.classifiers.tags.list.empty_state.title')}
					icon="es-person-boxes"
				/>
			) : (
				<MantineTable<TagRes>
					getRowId={(row) => row.id}
					columns={columns}
					isLoading={isLoading}
					dataOperations={mantineTableDataOperations}
					data={tags}
					enableRowActions={false}
					enablePagination={false}
					enableTopToolbar={false}
					enableFilters={false}
					enableGlobalFilter={false}
					enableRowSelection={false}
					manualPagination={false}
					manualSorting={false}
				/>
			)}

			<CreateTagDrawer
				opened={isCreateOpen}
				onClose={() => setIsCreateOpen(false)}
				onCreate={handleCreate}
				isLoading={isCreating}
			/>

			<ConfirmDeleteDrawer
				opened={!!tagToDelete}
				onClose={closeDeleteConfirm}
				title={tMipManage('root.classifiers.tags.delete_confirm.title')}
				alertTitle={
					attachedCohortCount > 0
						? tMipManage('root.classifiers.tags.delete_confirm.in_use_warning_title')
						: undefined
				}
				alertDescription={
					attachedCohortCount > 0
						? tMipManage('root.classifiers.tags.delete_confirm.in_use_warning', {
								count: attachedCohortCount,
							})
						: undefined
				}
				confirmDeleteText={tagToDelete?.name ?? ''}
				wordMismatchErrorMessage={tMipManage(
					'root.classifiers.tags.delete_confirm.word_mismatch'
				)}
				inputLabel={tMipManage('root.classifiers.tags.delete_confirm.input_label', {
					name: tagToDelete?.name ?? '',
				})}
				inputPlaceholder={tagToDelete?.name ?? ''}
				deleteBtnText={tMipManage('root.classifiers.tags.delete_confirm.confirm_button')}
				cancelBtnText={tMipManage('root.classifiers.tags.delete_confirm.cancel_button')}
				deleteSuccess={deleteDidSucceed}
				deleteLoading={isDeleting}
				handleDelete={handleConfirmDelete}
				enableDeleteButtonOnlyOnConfirmDelete
			/>
		</Flex>
	);
};
